import { useMemo } from "react"

import { useTranslation } from "react-i18next"

import type { DashboardUseCase } from "../../types/use-case.types"
import type { LucideIcon } from "@/components/icons"

import {
  Banknote,
  Calculator,
  Clock,
  DollarSign,
  Layers,
  ShieldCheck,
  TrendingUp,
} from "@/components/icons"
import { Card, CardContent } from "@/components/ui/card"
import { cn } from "@/lib/utils"

import { formatNumber, formatUSD } from "../../utils/format-helpers"

function getScoreColor(score: number | null): string {
  if (score == null) return "text-muted-foreground"
  if (score >= 80) return "text-success"
  if (score >= 60) return "text-warning"
  return "text-destructive"
}

type DashboardKpisProps = {
  useCases: DashboardUseCase[]
}

function KpiCard({
  icon: Icon,
  label,
  value,
  sub,
  className,
}: {
  icon: LucideIcon
  label: string
  value: string
  sub?: string
  className?: string
}) {
  return (
    <Card className="rounded-lg shadow-sm">
      <CardContent className="flex items-center gap-4 p-5">
        <div className="bg-muted rounded-xl p-3">
          <Icon className="size-6" />
        </div>
        <div className="flex-1">
          <div className={cn("text-2xl leading-tight font-semibold", className)}>{value}</div>
          <div className="text-muted-foreground text-sm">{label}</div>
          {sub ? <div className="text-muted-foreground/80 mt-1 text-xs">{sub}</div> : null}
        </div>
      </CardContent>
    </Card>
  )
}

export function DashboardKpis({ useCases }: DashboardKpisProps) {
  const { t } = useTranslation()

  const kpis = useMemo(() => {
    const count = useCases.length
    let totalInvestment = 0
    let totalOpCost = 0
    let totalHours = 0
    let totalBenefit = 0
    let totalRoi = 0
    let qaScoreSum = 0
    let qaScoreCount = 0

    for (const uc of useCases) {
      totalInvestment += parseFloat(uc.investment) || 0
      totalOpCost += uc.opCostYearly
      totalHours += uc.totalCapacityHours
      totalBenefit += uc.annualBenefit
      totalRoi += uc.roi12m
      if (uc.qaScore != null) {
        qaScoreSum += uc.qaScore
        qaScoreCount++
      }
    }

    const avgQaScore = qaScoreCount > 0 ? Math.round(qaScoreSum / qaScoreCount) : null

    return {
      count,
      totalInvestment,
      totalOpCost,
      totalHours,
      totalBenefit,
      totalRoi,
      avgQaScore,
      qaScoreCount,
    }
  }, [useCases])

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <KpiCard
        icon={Layers}
        label={t("useCases.dashboard.kpis.useCases")}
        sub={t("useCases.dashboard.kpis.totalLoaded")}
        value={formatNumber(kpis.count)}
      />
      <KpiCard
        icon={Banknote}
        label={t("useCases.dashboard.kpis.totalInvestment")}
        sub={t("useCases.dashboard.kpis.oneTime")}
        value={formatUSD(kpis.totalInvestment)}
      />
      <KpiCard
        icon={Calculator}
        label={t("useCases.dashboard.kpis.totalOpCost")}
        sub={t("useCases.dashboard.kpis.recurringAnnual")}
        value={formatUSD(kpis.totalOpCost)}
      />
      <KpiCard
        icon={Clock}
        label={t("useCases.dashboard.kpis.totalHours")}
        sub={t("useCases.dashboard.kpis.acrossAllUseCases")}
        value={formatNumber(Math.round(kpis.totalHours))}
      />
      <KpiCard
        icon={DollarSign}
        label={t("useCases.dashboard.kpis.totalBenefit")}
        sub={t("useCases.dashboard.kpis.softPlusHard")}
        value={formatUSD(kpis.totalBenefit)}
      />
      <KpiCard
        className={kpis.totalRoi >= 0 ? "text-success" : "text-destructive"}
        icon={TrendingUp}
        label={t("useCases.dashboard.kpis.totalRoi")}
        sub={t("useCases.dashboard.kpis.benefitMinusCosts")}
        value={formatUSD(kpis.totalRoi)}
      />
      <KpiCard
        className={getScoreColor(kpis.avgQaScore)}
        icon={ShieldCheck}
        label={t("useCases.dashboard.kpis.avgQaScore")}
        value={kpis.avgQaScore != null ? `${kpis.avgQaScore}` : "-"}
        sub={
          kpis.qaScoreCount > 0
            ? t("useCases.dashboard.kpis.reviewedCount", {
                reviewed: kpis.qaScoreCount,
                total: kpis.count,
              })
            : t("useCases.dashboard.kpis.noReviewsYet")
        }
      />
    </div>
  )
}
